export type PartnerAccessProjectId = number | string;

export interface PartnerAccessAccount {
  via_partner_access?: boolean;
  partner_access_project_id?: PartnerAccessProjectId;
  partner_access_conversation_id?: string;
  [key: string]: any;
}

export const isPartnerAccessAccount = (account?: PartnerAccessAccount | null): boolean =>
  account?.via_partner_access === true;

export const getPartnerAccessProjectId = (
  account?: PartnerAccessAccount | null,
): PartnerAccessProjectId | undefined => {
  if (!isPartnerAccessAccount(account)) return undefined;
  const projectId = account?.partner_access_project_id;
  if (projectId == null || String(projectId).trim() === '') return undefined;
  return projectId;
};

export const getPartnerAccessConversationId = (
  account?: PartnerAccessAccount | null,
): string | undefined => {
  if (!isPartnerAccessAccount(account)) return undefined;
  const conversationId = String(account?.partner_access_conversation_id || '').trim();
  return conversationId || undefined;
};

/**
 * The complete root state for a partner-to-customer plan handoff.
 *
 * This is intentionally nested state, rather than a Main route plus nested
 * navigation params. `navigationRef.resetRoot` can install this state in one
 * root-targeted action, so no child navigator has to consume a second action
 * while AppShell is swapping from the partner shell to the customer shell.
 */
export const buildPartnerAccessRootState = (
  projectId: PartnerAccessProjectId,
  conversationId?: string,
) => ({
  index: 0,
  routes: [
    {
      name: 'Main',
      state: {
        index: 0,
        routes: [
          {
            name: 'Plans',
            state: {
              index: 0,
              routes: [
                conversationId
                  ? { name: 'Conversation', params: { projectId, conversationId } }
                  : { name: 'ProjectChat', params: { projectId } },
              ],
            },
          },
        ],
      },
    },
  ],
});

export const shouldOpenCustomerPlanDirectly = (
  customer: { projects_count?: number | string | null } | null | undefined,
  canAccessPlans: boolean,
): boolean => canAccessPlans && Number(customer?.projects_count) === 1;

export const getSingleCustomerProject = (projects: unknown): any | undefined =>
  Array.isArray(projects) && projects.length === 1 ? projects[0] : undefined;
